import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, ExternalLink, Pencil, Calendar, MapPin, Building2, Star, Crown, ClipboardCheck, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useUser } from '@/lib/auth';

export default function AdminRevisione() {
  const user = useUser();
  const canPublish = !!(user?.is_super_admin || user?.permissions?.can_publish_events);
  const [items, setItems] = useState([]);
  const [locali, setLocali] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    const r = await api.get('/admin/eventi', { params: { stato: 'In Revisione' } });
    setItems(r.data || []); setLoading(false);
  };

  useEffect(() => {
    api.get('/admin/locali').then((r) => setLocali(r.data || []));
    load();
  }, []);

  const localeMap = Object.fromEntries(locali.map(l => [l.id, l]));

  const approve = async (e) => {
    setSavingId(e.id);
    try {
      await api.post(`/admin/eventi/${e.id}/approve`);
      toast.success(`“${e.titolo}” pubblicato`);
      setItems(prev => prev.filter(x => x.id !== e.id));
    } catch (err) { toast.error('Errore approvazione'); }
    finally { setSavingId(null); }
  };

  const updateField = async (id, patch) => {
    setSavingId(id);
    try {
      const r = await api.patch(`/admin/eventi/${id}`, patch);
      setItems(prev => prev.map(x => x.id === id ? r.data : x));
      toast.success('Aggiornato');
    } catch (err) { toast.error('Errore'); }
    finally { setSavingId(null); }
  };

  return (
    <AdminLayout title="Coda Revisione">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-[color:var(--gold)]"/>
          <p className="text-sm text-[color:var(--text-muted)]">Eventi in attesa di pubblicazione: <span className="text-[color:var(--gold)] font-semibold">{items.length}</span></p>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[color:var(--text-muted)]">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="py-16 rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)] text-center" data-testid="revisione-empty">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-3"/>
          <h3 className="font-heading text-2xl mb-1">Tutto approvato</h3>
          <p className="text-sm text-[color:var(--text-muted)]">Ottimo lavoro! Nessun evento in revisione.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((e) => (
            <div key={e.id} className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] overflow-hidden" data-testid="revisione-row">
              <div className="grid lg:grid-cols-[140px_1fr] gap-4">
                <div className="aspect-[16/9] lg:aspect-auto bg-[color:var(--surface-2)] flex-shrink-0">
                  {e.locandina ? <img src={e.locandina} alt={e.titolo} className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-[color:var(--gold)] opacity-40"><Building2 className="w-8 h-8"/></div>}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <h3 className="font-heading text-xl mb-1">{e.titolo}</h3>
                      <div className="flex items-center gap-3 text-xs text-[color:var(--text-muted)] flex-wrap">
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[color:var(--gold)]"/>{e.data} • {e.orario}</span>
                        <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5 text-[color:var(--gold)]"/>{localeMap[e.locale_id]?.nome || '-'}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[color:var(--gold)]"/>{e.zona}</span>
                        {e.source === 'webhook' && <Badge variant="outline" className="border-cyan-500/40 text-cyan-300">Webhook</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/admin/eventi/${e.id}/modifica`}><Button size="sm" variant="ghost"><Pencil className="w-4 h-4 mr-1"/>Modifica</Button></Link>
                      {canPublish ? (
                        <Button size="sm" data-testid="review-approve-button" onClick={() => approve(e)} disabled={savingId === e.id} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold btn-press"><CheckCircle2 className="w-4 h-4 mr-1"/>APPROVA</Button>
                      ) : (
                        <Button size="sm" disabled className="bg-gray-700 text-gray-400 cursor-not-allowed" data-testid="review-approve-locked"><Lock className="w-4 h-4 mr-1"/>Permesso mancante</Button>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3 mt-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1 block">Link Acquisto</Label>
                      <div className="flex gap-1">
                        <Input defaultValue={e.link_acquisto || ''} placeholder="https://…" data-testid="review-link-acquisto" onBlur={(ev) => { if (ev.target.value !== (e.link_acquisto || '')) updateField(e.id, { link_acquisto: ev.target.value }); }} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/>
                        {e.link_acquisto && <a href={e.link_acquisto} target="_blank" rel="noreferrer" className="flex items-center px-2 text-[color:var(--gold)]"><ExternalLink className="w-4 h-4"/></a>}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1 block">Link Tavolo</Label>
                      <div className="flex gap-1">
                        <Input defaultValue={e.link_tavolo || ''} placeholder="https://…" data-testid="review-link-tavolo" onBlur={(ev) => { if (ev.target.value !== (e.link_tavolo || '')) updateField(e.id, { link_tavolo: ev.target.value }); }} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/>
                        {e.link_tavolo && <a href={e.link_tavolo} target="_blank" rel="noreferrer" className="flex items-center px-2 text-[color:var(--gold)]"><ExternalLink className="w-4 h-4"/></a>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-4 flex-wrap">
                    <ToggleRow icon={Star} label="In Evidenza" testId="review-toggle-featured" checked={!!e.in_evidenza} disabled={!canPublish} onChange={(v) => updateField(e.id, { in_evidenza: v })} />
                    <ToggleRow icon={Crown} label="Tavolo Privé" testId="review-toggle-prive" checked={!!e.tavolo_prive} onChange={(v) => updateField(e.id, { tavolo_prive: v })} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

function ToggleRow({ icon: Icon, label, testId, checked, onChange, disabled }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-[color:var(--border-c)] bg-[color:var(--surface-2)] ${disabled ? 'opacity-50' : ''}`}>
      <Icon className="w-4 h-4 text-[color:var(--gold)]" />
      <Label className="text-sm">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} disabled={disabled} />
    </div>
  );
}
